import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Search, Trash2, Zap, Flame, Upload, FileText, ExternalLink, Send, CheckCircle, XCircle, Ban, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LUCE e GAS → Pratiche (backend: routes/pratiche_luce_gas.py). Cambio fornitore/offerta per un
// POD/PDR; quando la pratica è ATTIVA la scadenza delle condizioni finisce in LUCE E GAS → Scadenze.

export const STATI_PRATICA = {
  da_inviare: { label: 'Da inviare', classe: 'bg-slate-100 text-slate-700' },
  inviata: { label: 'Inviata', classe: 'bg-sky-100 text-sky-800' },
  attiva: { label: 'Attiva', classe: 'bg-emerald-100 text-emerald-800' },
  rifiutata: { label: 'Rifiutata', classe: 'bg-red-100 text-red-700' },
  annullata: { label: 'Annullata', classe: 'bg-zinc-200 text-zinc-700' },
  sostituita: { label: 'Sostituita', classe: 'bg-violet-100 text-violet-800' },
};

const OPERAZIONI = [
  ['cambio_fornitore', 'CAMBIO FORNITORE'], ['cambio_offerta', 'CAMBIO OFFERTA'], ['voltura', 'VOLTURA'],
  ['subentro', 'SUBENTRO'], ['nuovo_allaccio', 'NUOVO ALLACCIO'],
];
const TIPI_ALLEGATO = ['CONTRATTO', 'CTE', 'BOLLETTA', 'DOCUMENTO', 'VISURA', 'ALTRO'];

const dataIt = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');
const eur = (v) => (v === null || v === undefined ? '' : v.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }));
const VUOTA = {
  client_id: '', asset_id: '', tipo_utenza: 'luce', pod_pdr: '', indirizzo_fornitura: '', tipologia_uso: '',
  operazione: 'cambio_fornitore', fornitore_da: '', offerta_da: '', fornitore_a: '', offerta_a: '', codice_offerta_a: '',
  tipo_prezzo: '', durata_mesi: 12, consumo_annuo: null, risparmio_annuo: null, codice_contratto: '',
  data_firma: '', data_invio: '', data_attivazione: '', data_scadenza: '', scadenza_manuale: false, note: '',
};

function Campo({ label, children, largo }) {
  return (
    <div className={largo ? 'col-span-2' : ''}>
      <Label className="text-xs text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function FormPratica({ v, set }) {
  const t = (k, upper = true) => (
    <Input className={`h-8 ${upper ? 'uppercase' : ''}`} value={v[k] ?? ''} onChange={(e) => set({ [k]: upper ? e.target.value.toUpperCase() : e.target.value })} />
  );
  const d = (k) => <Input type="date" className="h-8" value={v[k] || ''} onChange={(e) => set({ [k]: e.target.value })} />;
  const n = (k) => (
    <Input inputMode="decimal" className="h-8" value={v[k] ?? ''}
      onChange={(e) => { const x = e.target.value.replace(',', '.'); set({ [k]: x === '' ? null : Number(x) }); }} />
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Campo label="Tipo">
        <select className="w-full h-8 border rounded px-2 text-sm" value={v.tipo_utenza} onChange={(e) => set({ tipo_utenza: e.target.value })}>
          <option value="luce">LUCE</option><option value="gas">GAS</option>
        </select>
      </Campo>
      <Campo label="Operazione">
        <select className="w-full h-8 border rounded px-2 text-sm" value={v.operazione} onChange={(e) => set({ operazione: e.target.value })}>
          {OPERAZIONI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Campo>
      <Campo label={v.tipo_utenza === 'gas' ? 'PDR' : 'POD'}>{t('pod_pdr')}</Campo>
      <Campo label="Uso">{t('tipologia_uso')}</Campo>
      <Campo label="Indirizzo fornitura" largo>{t('indirizzo_fornitura')}</Campo>
      <Campo label="Fornitore attuale">{t('fornitore_da')}</Campo>
      <Campo label="Offerta attuale">{t('offerta_da')}</Campo>
      <Campo label="Nuovo fornitore">{t('fornitore_a')}</Campo>
      <Campo label="Nuova offerta">{t('offerta_a')}</Campo>
      <Campo label="Codice offerta">{t('codice_offerta_a')}</Campo>
      <Campo label="Prezzo">
        <select className="w-full h-8 border rounded px-2 text-sm" value={v.tipo_prezzo || ''} onChange={(e) => set({ tipo_prezzo: e.target.value })}>
          <option value="">—</option><option value="fisso">FISSO</option><option value="variabile">VARIABILE</option>
        </select>
      </Campo>
      <Campo label="N. contratto / proposta">{t('codice_contratto')}</Campo>
      <Campo label="Durata condizioni (mesi)">{n('durata_mesi')}</Campo>
      <Campo label={`Consumo annuo ${v.tipo_utenza === 'gas' ? 'Smc' : 'kWh'}`}>{n('consumo_annuo')}</Campo>
      <Campo label="Risparmio stimato €/anno">{n('risparmio_annuo')}</Campo>
      <Campo label="Data firma">{d('data_firma')}</Campo>
      <Campo label="Data invio">{d('data_invio')}</Campo>
      <Campo label="Data attivazione">{d('data_attivazione')}</Campo>
      <Campo label="Scadenza condizioni">
        <Input type="date" className="h-8" value={v.data_scadenza || ''} disabled={!v.scadenza_manuale} onChange={(e) => set({ data_scadenza: e.target.value })} />
        <label className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5">
          <input type="checkbox" checked={!!v.scadenza_manuale} onChange={(e) => set({ scadenza_manuale: e.target.checked })} />
          a mano (altrimenti attivazione + durata)
        </label>
      </Campo>
      <Campo label="Note" largo>
        <textarea className="w-full border rounded p-2 text-sm min-h-[60px]" value={v.note || ''} onChange={(e) => set({ note: e.target.value })} />
      </Campo>
    </div>
  );
}

export default function PraticheLuceGas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [conteggi, setConteggi] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [stato, setStato] = useState('');
  const [tipo, setTipo] = useState('');
  const [dettaglio, setDettaglio] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [dataEvento, setDataEvento] = useState('');
  const [notaEvento, setNotaEvento] = useState('');
  const [tipoAllegato, setTipoAllegato] = useState('CONTRATTO');
  const [nuova, setNuova] = useState(null);
  const [cercaCliente, setCercaCliente] = useState('');
  const [clienti, setClienti] = useState([]);
  const [utenze, setUtenze] = useState([]);

  const fetchItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/luce-gas/pratiche`, { params: { search, stato, tipo } });
      setItems(r.data.items || []);
      setConteggi(r.data.conteggi || {});
    } catch (e) {
      toast.error('Errore nel caricamento delle pratiche');
    } finally {
      setLoading(false);
    }
  }, [search, stato, tipo]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const apri = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/luce-gas/pratiche/${id}`);
      setDettaglio({ ...r.data });
      setDataEvento('');
      setNotaEvento('');
    } catch (_) {
      toast.error('Pratica non trovata');
    }
  }, []);

  // ?apri=ID (da Simulazione offerta, Scadenze, Fatture estrazione)
  useEffect(() => {
    const id = searchParams.get('apri');
    if (id) {
      apri(id);
      searchParams.delete('apri');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, apri]);

  const salva = async () => {
    setSalvando(true);
    try {
      const { id, numero, stato: _s, storico, allegati, cliente_nome, giorni_alla_scadenza, created_at, user_id, operatore, ...campi } = dettaglio;
      const r = await axios.put(`${API}/luce-gas/pratiche/${id}`, campi);
      setDettaglio((d) => ({ ...d, ...r.data }));
      toast.success('Pratica salvata');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSalvando(false);
    }
  };

  const cambiaStato = async (nuovo) => {
    if (nuovo === 'attiva' && !dataEvento && !dettaglio.data_attivazione) {
      toast.error('Indica la data di attivazione');
      return;
    }
    try {
      await salva();
      const r = await axios.post(`${API}/luce-gas/pratiche/${dettaglio.id}/stato`, { stato: nuovo, data: dataEvento, nota: notaEvento });
      setDettaglio((d) => ({ ...d, ...r.data }));
      setDataEvento('');
      setNotaEvento('');
      toast.success(`Pratica: ${STATI_PRATICA[nuovo].label.toLowerCase()}`);
      fetchItems();
      window.dispatchEvent(new Event('jvservice:scadenze-luce-gas'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const elimina = async () => {
    if (!window.confirm(`Eliminare la pratica ${dettaglio.numero}?`)) return;
    await axios.delete(`${API}/luce-gas/pratiche/${dettaglio.id}`).catch(() => toast.error('Errore'));
    setDettaglio(null);
    fetchItems();
  };

  const caricaAllegati = async (files) => {
    for (const f of Array.from(files || [])) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('tipo', tipoAllegato);
      try {
        const r = await axios.post(`${API}/luce-gas/pratiche/${dettaglio.id}/allegati`, fd);
        setDettaglio((d) => ({ ...d, allegati: [...(d.allegati || []), r.data] }));
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || 'caricamento non riuscito'}`);
      }
    }
  };

  const apriAllegato = async (a) => {
    const r = await axios.get(`${API}/luce-gas/pratiche/${dettaglio.id}/allegati/${a.id}`, { responseType: 'blob' }).catch(() => null);
    if (r) window.open(URL.createObjectURL(r.data), '_blank');
    else toast.error('Allegato non disponibile');
  };

  const eliminaAllegato = async (a) => {
    if (!window.confirm(`Eliminare ${a.nome}?`)) return;
    await axios.delete(`${API}/luce-gas/pratiche/${dettaglio.id}/allegati/${a.id}`).catch(() => toast.error('Errore'));
    setDettaglio((d) => ({ ...d, allegati: d.allegati.filter((x) => x.id !== a.id) }));
  };

  // nuova pratica: cliente → utenza (dai beni in anagrafica) → dati
  useEffect(() => {
    if (!nuova || nuova.client_id || cercaCliente.trim().length < 2) { setClienti([]); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API}/clients`, { params: { page: 1, page_size: 10, search: cercaCliente } })
        .then((r) => setClienti(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [cercaCliente, nuova]);

  const scegliCliente = async (c) => {
    setNuova((n) => ({ ...n, client_id: c.id, _nome: c.company_name || `${c.last_name || ''} ${c.first_name || ''}`.trim() }));
    try {
      const r = await axios.get(`${API}/assets`, { params: { client_id: c.id } });
      const lista = (Array.isArray(r.data) ? r.data : (r.data.items || [])).filter((a) => a.asset_type === 'utenze'
        && ['luce', 'gas'].includes((a.tipo_utenza || '').toLowerCase()));
      setUtenze(lista);
    } catch (_) {
      setUtenze([]);
    }
  };

  const scegliUtenza = (a) => setNuova((n) => ({
    ...n, asset_id: a.id, tipo_utenza: (a.tipo_utenza || 'luce').toLowerCase(), pod_pdr: (a.pod_pdr_codice || '').toUpperCase(),
    indirizzo_fornitura: (a.indirizzo_fornitura || '').toUpperCase(), fornitore_da: (a.fornitore || '').toUpperCase(),
  }));

  const creaNuova = async () => {
    if (!nuova.client_id) { toast.error('Scegli il cliente'); return; }
    setSalvando(true);
    try {
      const { _nome, ...dati } = nuova;
      const r = await axios.post(`${API}/luce-gas/pratiche`, dati);
      setNuova(null);
      fetchItems();
      apri(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setSalvando(false);
    }
  };

  const totale = Object.values(conteggi).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pratiche-luce-gas-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Pratiche luce e gas</h1>
          <p className="text-slate-600 mt-1">Cambi di fornitore e di offerta: invio, attivazione e scadenza delle condizioni</p>
        </div>
        <Button type="button" className="bg-amber-600 hover:bg-amber-700" onClick={() => { setNuova({ ...VUOTA }); setCercaCliente(''); setUtenze([]); }}>
          <Plus className="w-4 h-4 mr-2" />Nuova pratica
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant={stato === '' ? 'default' : 'outline'} onClick={() => setStato('')}>TUTTE ({totale})</Button>
            {Object.entries(STATI_PRATICA).map(([k, s]) => (
              <Button key={k} type="button" size="sm" variant={stato === k ? 'default' : 'outline'} onClick={() => setStato(k)}>
                {s.label.toUpperCase()} ({conteggi[k] || 0})
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input className="pl-8" placeholder="Cerca cliente, POD/PDR, numero, fornitore…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            {[['', 'TUTTE'], ['luce', 'LUCE'], ['gas', 'GAS']].map(([k, l]) => (
              <Button key={k} type="button" size="sm" variant={tipo === k ? 'default' : 'outline'} onClick={() => setTipo(k)}>{l}</Button>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b border-slate-900">
                <tr>
                  <th className="text-left p-2">Numero</th><th className="text-left p-2">Cliente</th><th className="text-left p-2">POD / PDR</th>
                  <th className="text-left p-2">Da → A</th><th className="text-left p-2">Stato</th><th className="text-left p-2">Attivazione</th>
                  <th className="text-left p-2">Scadenza</th><th className="text-right p-2">Risparmio</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
                {!loading && items.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-500">Nessuna pratica</td></tr>}
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-slate-900 hover:bg-slate-50 cursor-pointer" onClick={() => apri(p.id)}>
                    <td className="p-2 font-mono text-xs whitespace-nowrap">{p.numero}</td>
                    <td className="p-2 font-medium whitespace-nowrap">{p.cliente_nome}</td>
                    <td className="p-2 whitespace-nowrap">
                      {p.tipo_utenza === 'gas' ? <Flame className="w-4 h-4 text-orange-500 inline mr-1" /> : <Zap className="w-4 h-4 text-yellow-500 inline mr-1" />}
                      {p.pod_pdr}
                    </td>
                    <td className="p-2 text-xs">
                      <div className="text-slate-500">{p.fornitore_da} {p.offerta_da}</div>
                      <div className="font-medium">→ {p.fornitore_a} {p.offerta_a}</div>
                    </td>
                    <td className="p-2"><Badge className={STATI_PRATICA[p.stato]?.classe}>{STATI_PRATICA[p.stato]?.label}</Badge></td>
                    <td className="p-2 whitespace-nowrap">{dataIt(p.data_attivazione)}</td>
                    <td className={`p-2 whitespace-nowrap ${p.giorni_alla_scadenza !== undefined && p.giorni_alla_scadenza <= 30 ? 'text-red-600 font-medium' : ''}`}>
                      {dataIt(p.data_scadenza)}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">{p.risparmio_annuo != null ? eur(p.risparmio_annuo) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* NUOVA PRATICA */}
      <Dialog open={!!nuova} onOpenChange={(o) => { if (!o) setNuova(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova pratica luce / gas</DialogTitle>
            <DialogDescription>Scegli il cliente e l'utenza; le pratiche si creano anche da Simulazione offerta e da Fatture estrazione</DialogDescription>
          </DialogHeader>
          {nuova && (
            <div className="space-y-4">
              {!nuova.client_id ? (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Cliente (in anagrafica)</Label>
                  <Input autoFocus placeholder="Cognome, ragione sociale, CF, P.IVA…" value={cercaCliente} onChange={(e) => setCercaCliente(e.target.value)} />
                  <div className="divide-y border rounded max-h-60 overflow-y-auto">
                    {clienti.map((c) => (
                      <button key={c.id} type="button" className="w-full text-left p-2 hover:bg-slate-50 text-sm" onClick={() => scegliCliente(c)}>
                        <span className="font-medium">{c.company_name || `${c.last_name || ''} ${c.first_name || ''}`}</span>
                        <span className="text-slate-500 ml-2 text-xs">{c.vat_number || c.fiscal_code}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500">Cliente:</span><span className="font-semibold">{nuova._nome}</span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => { setNuova((n) => ({ ...n, client_id: '', asset_id: '' })); setUtenze([]); }}>cambia</Button>
                </div>
              )}
              {nuova.client_id && utenze.length > 0 && (
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="text-slate-500">Utenze del cliente:</span>
                  {utenze.map((a) => (
                    <Button key={a.id} type="button" size="sm" variant={nuova.asset_id === a.id ? 'default' : 'outline'} onClick={() => scegliUtenza(a)}>
                      {(a.tipo_utenza || '').toUpperCase()} {a.pod_pdr_codice}
                    </Button>
                  ))}
                </div>
              )}
              {nuova.client_id && <FormPratica v={nuova} set={(x) => setNuova((n) => ({ ...n, ...x }))} />}
              <div className="flex justify-end">
                <Button type="button" onClick={creaNuova} disabled={salvando || !nuova.client_id}>
                  {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Crea pratica
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DETTAGLIO */}
      <Dialog open={!!dettaglio} onOpenChange={(o) => { if (!o) setDettaglio(null); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          {dettaglio && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  {dettaglio.numero} — {dettaglio.cliente_nome}
                  <Badge className={STATI_PRATICA[dettaglio.stato]?.classe}>{STATI_PRATICA[dettaglio.stato]?.label}</Badge>
                </DialogTitle>
                <DialogDescription className="flex flex-wrap gap-3">
                  <span>Creata il {dataIt(dettaglio.created_at)} da {dettaglio.operatore}</span>
                  <Link className="text-blue-600 inline-flex items-center gap-1" to={`/clients?open=${dettaglio.client_id}${dettaglio.asset_id ? `&asset=${dettaglio.asset_id}` : ''}`}>
                    <ExternalLink className="w-3.5 h-3.5" />Apri anagrafica
                  </Link>
                  {dettaglio.giorni_alla_scadenza !== undefined && (
                    <span className={dettaglio.giorni_alla_scadenza < 0 ? 'text-red-600 font-medium' : ''}>
                      {dettaglio.giorni_alla_scadenza < 0 ? `Condizioni scadute da ${-dettaglio.giorni_alla_scadenza} giorni` : `Scadenza tra ${dettaglio.giorni_alla_scadenza} giorni`}
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>

              {/* avanzamento */}
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <div className="text-xs font-semibold text-amber-800">AVANZAMENTO</div>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Data (vuota = oggi)</Label>
                    <Input type="date" className="h-8 w-40 bg-white" value={dataEvento} onChange={(e) => setDataEvento(e.target.value)} />
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <Label className="text-xs text-slate-500">Nota</Label>
                    <Input className="h-8 bg-white" value={notaEvento} onChange={(e) => setNotaEvento(e.target.value)} placeholder="es. motivo del rifiuto" />
                  </div>
                  {dettaglio.stato === 'da_inviare' && (
                    <Button type="button" size="sm" onClick={() => cambiaStato('inviata')}><Send className="w-4 h-4 mr-1" />Inviata al fornitore</Button>
                  )}
                  {['da_inviare', 'inviata'].includes(dettaglio.stato) && (
                    <Button type="button" size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => cambiaStato('attiva')}>
                      <CheckCircle className="w-4 h-4 mr-1" />Attivata
                    </Button>
                  )}
                  {['da_inviare', 'inviata'].includes(dettaglio.stato) && (
                    <Button type="button" size="sm" variant="outline" className="text-red-700" onClick={() => cambiaStato('rifiutata')}>
                      <XCircle className="w-4 h-4 mr-1" />Rifiutata
                    </Button>
                  )}
                  {['da_inviare', 'inviata'].includes(dettaglio.stato) && (
                    <Button type="button" size="sm" variant="outline" onClick={() => cambiaStato('annullata')}><Ban className="w-4 h-4 mr-1" />Annullata</Button>
                  )}
                  {['rifiutata', 'annullata', 'sostituita', 'attiva'].includes(dettaglio.stato) && (
                    <Button type="button" size="sm" variant="outline" onClick={() => cambiaStato('da_inviare')}>Riporta a "da inviare"</Button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500">Con "Attivata" la scadenza = data di attivazione + durata in mesi; la pratica entra in LUCE E GAS → Scadenze.</p>
              </div>

              <FormPratica v={dettaglio} set={(x) => setDettaglio((d) => ({ ...d, ...x }))} />

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-slate-500">ALLEGATI</div>
                  <div className="flex gap-2">
                    <select className="h-9 border rounded px-2 text-sm" value={tipoAllegato} onChange={(e) => setTipoAllegato(e.target.value)}>
                      {TIPI_ALLEGATO.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <label className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded text-sm text-slate-600 hover:bg-slate-50 cursor-pointer h-9">
                      <Upload className="w-4 h-4" />Trascina o scegli i file
                      <input type="file" multiple accept="application/pdf,image/jpeg,image/png" className="hidden"
                        onChange={(e) => { caricaAllegati(e.target.files); e.target.value = ''; }} />
                    </label>
                  </div>
                  <div className="divide-y border rounded">
                    {(dettaglio.allegati || []).length === 0 && <div className="p-2 text-sm text-slate-500">Nessun allegato</div>}
                    {(dettaglio.allegati || []).map((a) => (
                      <div key={a.id} className="p-2 flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <Badge variant="outline" className="shrink-0">{a.tipo}</Badge>
                        <button type="button" className="truncate text-left text-blue-700 hover:underline flex-1" onClick={() => apriAllegato(a)}>{a.nome}</button>
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => eliminaAllegato(a)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-slate-500">STORICO</div>
                  <div className="border rounded divide-y max-h-56 overflow-y-auto">
                    {[...(dettaglio.storico || [])].reverse().map((s, i) => (
                      <div key={i} className="p-2 text-sm">
                        <Badge className={STATI_PRATICA[s.stato]?.classe}>{STATI_PRATICA[s.stato]?.label}</Badge>
                        <span className="ml-2 text-slate-600">{dataIt(s.data || s.quando)}</span>
                        <span className="ml-2 text-xs text-slate-400">{s.utente}</span>
                        {s.nota && <div className="text-xs text-slate-600 mt-0.5">{s.nota}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button type="button" variant="outline" className="text-red-700" onClick={elimina}><Trash2 className="w-4 h-4 mr-2" />Elimina pratica</Button>
                <Button type="button" onClick={salva} disabled={salvando}>
                  {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Salva
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
